/**
 * Projection between a product version's contact-type field types and real `Contact`
 * columns (docs/plan/product-version-contact-types/plan.md D1).
 *
 * `ProductVersionContactTypeFieldType` is a closed, semantic enum — "which standard
 * contact attributes does this type collect" — so every value projects onto columns that
 * already exist on `Contact` (plus the three added for it: `companyName`,
 * `relationshipToClaimant`, `witnessStatement`). Storing in columns rather than a JSON bag
 * is what keeps product-defined contacts reachable by the comms contact resolver, email
 * routing, AI context, exports and dedup with no extra code.
 *
 * This file is the single source of truth for that projection. Adding a field type means
 * adding its columns here; the config editor, the mapping picker and the claim-detail
 * contact form all derive from it.
 */

import type { ProductVersionContactTypeFieldType } from '@shared/prisma/enums';

export type MappableContactFieldValueType = 'text' | 'date' | 'address';

export interface MappableContactField {
    /** `Contact` column name */
    key: string;
    /** UI label (sentence case) */
    label: string;
    valueType: MappableContactFieldValueType;
}

/**
 * Whitelist of `Contact` columns a product version's claim form fields may be mapped onto.
 * Keys deliberately equal the column names so the lodgement engine assigns directly.
 */
export const MAPPABLE_CONTACT_FIELDS = [
    { key: 'firstName', label: 'First name', valueType: 'text' },
    { key: 'lastName', label: 'Last name', valueType: 'text' },
    { key: 'phone', label: 'Phone number', valueType: 'text' },
    { key: 'email', label: 'Email address', valueType: 'text' },
    { key: 'addressLine1', label: 'Address line 1', valueType: 'text' },
    { key: 'addressLine2', label: 'Address line 2', valueType: 'text' },
    { key: 'addressSuburb', label: 'Suburb', valueType: 'text' },
    { key: 'addressState', label: 'State', valueType: 'text' },
    { key: 'addressPostcode', label: 'Postcode', valueType: 'text' },
    { key: 'addressCountry', label: 'Country', valueType: 'text' },
    { key: 'companyName', label: 'Company name', valueType: 'text' },
    { key: 'birthDate', label: 'Date of birth', valueType: 'date' },
    { key: 'relationshipToClaimant', label: 'Relationship to claimant', valueType: 'text' },
    { key: 'witnessStatement', label: 'Witness statement', valueType: 'text' },
    { key: 'note', label: 'Notes', valueType: 'text' },
] as const satisfies readonly MappableContactField[];

export type MappableContactFieldKey = (typeof MAPPABLE_CONTACT_FIELDS)[number]['key'];

export const MAPPABLE_CONTACT_FIELD_KEYS = MAPPABLE_CONTACT_FIELDS.map((field) => field.key) as [
    MappableContactFieldKey,
    ...MappableContactFieldKey[],
];

export const MAPPABLE_CONTACT_FIELDS_BY_KEY: Record<MappableContactFieldKey, MappableContactField> = Object.fromEntries(
    MAPPABLE_CONTACT_FIELDS.map((field) => [field.key, field]),
) as Record<MappableContactFieldKey, MappableContactField>;

export function isMappableContactFieldKey(key: string): key is MappableContactFieldKey {
    return key in MAPPABLE_CONTACT_FIELDS_BY_KEY;
}

/**
 * Composite contact targets write several columns of one contact from a single answer. Each
 * is stored in `contactFieldKey` like a column, expanded by the mapper, and covers the
 * required columns of the contact-type field it belongs to.
 *
 * The address composite takes an address element and fills every address column.
 */
export const CONTACT_ADDRESS_TARGET = {
    key: 'address',
    label: 'Address (all parts)',
    valueType: 'address',
    columns: {
        line1: 'addressLine1',
        line2: 'addressLine2',
        suburb: 'addressSuburb',
        state: 'addressState',
        postcode: 'addressPostcode',
        country: 'addressCountry',
    },
} as const satisfies MappableContactField & { columns: Record<string, MappableContactFieldKey> };

/**
 * The full-name composite takes one text answer ("Jordan Avery") and splits it on the last
 * space into first and last name, so a form with a single name field can satisfy a contact
 * type's required Full name.
 */
export const CONTACT_FULL_NAME_TARGET = {
    key: 'fullName',
    label: 'Full name (first and last)',
    valueType: 'text',
    columns: {
        firstName: 'firstName',
        lastName: 'lastName',
    },
} as const satisfies MappableContactField & { columns: Record<string, MappableContactFieldKey> };

export type ContactAddressTargetKey = typeof CONTACT_ADDRESS_TARGET.key;
export type ContactFullNameTargetKey = typeof CONTACT_FULL_NAME_TARGET.key;
export type ContactCompositeTargetKey = ContactAddressTargetKey | ContactFullNameTargetKey;

const CONTACT_COMPOSITE_TARGETS = {
    address: { target: CONTACT_ADDRESS_TARGET, fieldType: 'ADDRESS' },
    fullName: { target: CONTACT_FULL_NAME_TARGET, fieldType: 'FULL_NAME' },
} as const satisfies Record<
    ContactCompositeTargetKey,
    {
        target: MappableContactField & { columns: Record<string, MappableContactFieldKey> };
        fieldType: ProductVersionContactTypeFieldType;
    }
>;

export const CONTACT_COMPOSITE_TARGET_KEYS = [CONTACT_ADDRESS_TARGET.key, CONTACT_FULL_NAME_TARGET.key] as const;

/** A contact target key as stored: a column, or a composite. */
export type ContactTargetKey = MappableContactFieldKey | ContactCompositeTargetKey;

export const CONTACT_TARGET_KEYS = [...MAPPABLE_CONTACT_FIELD_KEYS, ...CONTACT_COMPOSITE_TARGET_KEYS] as [
    ContactTargetKey,
    ...ContactTargetKey[],
];

export function isContactAddressTargetKey(key: string): key is ContactAddressTargetKey {
    return key === CONTACT_ADDRESS_TARGET.key;
}

export function isContactCompositeTargetKey(key: string): key is ContactCompositeTargetKey {
    return (CONTACT_COMPOSITE_TARGET_KEYS as readonly string[]).includes(key);
}

export function isContactTargetKey(key: string): key is ContactTargetKey {
    return isMappableContactFieldKey(key) || isContactCompositeTargetKey(key);
}

/** The label and value type of any contact target, column or composite. */
export function contactTargetField(key: ContactTargetKey): MappableContactField {
    return isContactCompositeTargetKey(key)
        ? CONTACT_COMPOSITE_TARGETS[key].target
        : MAPPABLE_CONTACT_FIELDS_BY_KEY[key];
}

/** The contact columns a composite target writes, keyed by the part of the answer each takes. */
export function contactCompositeColumns(key: ContactCompositeTargetKey): Record<string, MappableContactFieldKey> {
    return CONTACT_COMPOSITE_TARGETS[key].target.columns;
}

/** Whether a contact type collects the field a composite target belongs to — its precondition. */
export function contactTypeCollectsComposite(
    key: ContactCompositeTargetKey,
    fieldTypes: readonly ProductVersionContactTypeFieldType[],
): boolean {
    return fieldTypes.includes(CONTACT_COMPOSITE_TARGETS[key].fieldType);
}

/** Whether a contact type collects an address at all — the address composite's precondition. */
export function contactTypeCollectsAddress(fieldTypes: readonly ProductVersionContactTypeFieldType[]): boolean {
    return contactTypeCollectsComposite(CONTACT_ADDRESS_TARGET.key, fieldTypes);
}

/**
 * Field type → the columns it collects.
 *
 * `all` is what the mapping picker offers and what the contact form renders.
 * `required` is the subset that must be filled when the field is marked `isRequired` —
 * a required address means line 1, suburb, state and postcode, not line 2 and country.
 */
export const CONTACT_TYPE_FIELD_COLUMNS: Record<
    ProductVersionContactTypeFieldType,
    { all: readonly MappableContactFieldKey[]; required: readonly MappableContactFieldKey[] }
> = {
    FULL_NAME: {
        all: ['firstName', 'lastName'],
        required: ['firstName', 'lastName'],
    },
    PHONE_NUMBER: {
        all: ['phone'],
        required: ['phone'],
    },
    EMAIL: {
        all: ['email'],
        required: ['email'],
    },
    ADDRESS: {
        all: ['addressLine1', 'addressLine2', 'addressSuburb', 'addressState', 'addressPostcode', 'addressCountry'],
        required: ['addressLine1', 'addressSuburb', 'addressState', 'addressPostcode'],
    },
    COMPANY_NAME: {
        all: ['companyName'],
        required: ['companyName'],
    },
    DATE_OF_BIRTH: {
        all: ['birthDate'],
        required: ['birthDate'],
    },
    RELATIONSHIP_TO_CLAIMANT: {
        all: ['relationshipToClaimant'],
        required: ['relationshipToClaimant'],
    },
    WITNESS_STATEMENT: {
        all: ['witnessStatement'],
        required: ['witnessStatement'],
    },
    NOTES: {
        all: ['note'],
        required: ['note'],
    },
};

/** Columns a contact type offers, given the field types it is configured with. */
export function getContactTypeColumns(
    fieldTypes: readonly ProductVersionContactTypeFieldType[],
): MappableContactFieldKey[] {
    const seen = new Set<MappableContactFieldKey>();

    for (const fieldType of fieldTypes) {
        for (const column of CONTACT_TYPE_FIELD_COLUMNS[fieldType].all) {
            seen.add(column);
        }
    }

    // Preserve MAPPABLE_CONTACT_FIELDS order so pickers and forms are stable regardless of
    // the order the contact type's fields happen to be configured in.
    return MAPPABLE_CONTACT_FIELDS.map((field) => field.key).filter((key) => seen.has(key));
}

/** Columns that must be filled, given the contact type's fields and their `isRequired` flags. */
export function getRequiredContactTypeColumns(
    fields: readonly { type: ProductVersionContactTypeFieldType; isRequired: boolean }[],
): MappableContactFieldKey[] {
    const seen = new Set<MappableContactFieldKey>();

    for (const field of fields) {
        if (!field.isRequired) {
            continue;
        }

        for (const column of CONTACT_TYPE_FIELD_COLUMNS[field.type].required) {
            seen.add(column);
        }
    }

    return MAPPABLE_CONTACT_FIELDS.map((field) => field.key).filter((key) => seen.has(key));
}

/**
 * Field types that identify a contact. Every contact type must collect at least one of
 * these, or it cannot identify anybody (plan D5).
 */
export const IDENTIFYING_CONTACT_FIELD_TYPES = [
    'FULL_NAME',
    'COMPANY_NAME',
] as const satisfies readonly ProductVersionContactTypeFieldType[];

export function hasIdentifyingContactField(fieldTypes: readonly ProductVersionContactTypeFieldType[]): boolean {
    return fieldTypes.some((fieldType) =>
        IDENTIFYING_CONTACT_FIELD_TYPES.includes(fieldType as (typeof IDENTIFYING_CONTACT_FIELD_TYPES)[number]),
    );
}
